import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, Sparkles, Globe, CheckCircle2, XCircle, AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, Markdown, Panel } from "@/components/common";
import { UpgradeGate } from "@/components/UpgradeGate";
import AdvisorChat from "@/components/AdvisorChat";
import ContentLibrary, { SEO_KINDS } from "@/components/ContentLibrary";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SEV = { high: "bg-[hsl(var(--destructive))] text-white", medium: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]", low: "bg-muted text-foreground" };

function ScoreRing({ score }) {
  const color = score >= 80 ? "hsl(var(--success))" : score >= 55 ? "hsl(var(--warning))" : "hsl(var(--destructive))";
  return (
    <div className="relative h-28 w-28 shrink-0" data-testid="seo-score">
      <svg viewBox="0 0 36 36" className="h-28 w-28 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="hsl(var(--muted))" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${score} 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-3xl font-semibold font-display tabular-nums">{score}</span><span className="text-[10px] text-muted-foreground">/ 100</span></div>
    </div>
  );
}

function Audit() {
  const { user } = useAuth();
  const [url, setUrl] = useState(user.company?.website || "");
  const [audits, setAudits] = useState([]);
  const [current, setCurrent] = useState(null);
  const [running, setRunning] = useState(false);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/seo/audits"); setAudits(data); setCurrent((c) => c || data[0] || null); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (e) => {
    e.preventDefault();
    setRunning(true);
    try { const { data } = await api.post("/seo/audits", { url }); setCurrent(data); setAudits((a) => [data, ...a]); toast.success(`Audit complete: score ${data.score}`); }
    catch (err) { toast.error(errMsg(err)); } finally { setRunning(false); }
  };
  const plan = async () => {
    setPlanning(true);
    try { const { data } = await api.post(`/seo/audits/${current.audit_id}/plan`); const upd = { ...current, ai_summary: data.ai_summary }; setCurrent(upd); setAudits((a) => a.map((x) => (x.audit_id === upd.audit_id ? upd : x))); }
    catch (err) { toast.error(errMsg(err)); } finally { setPlanning(false); }
  };
  const remove = async (a) => {
    await api.delete(`/seo/audits/${a.audit_id}`);
    setAudits((xs) => xs.filter((x) => x.audit_id !== a.audit_id));
    if (current?.audit_id === a.audit_id) setCurrent(null);
  };

  const counts = current ? ["high", "medium", "low"].map((s) => [s, current.issues.filter((i) => i.severity === s).length]) : [];

  return (
    <div className="space-y-4">
      <form onSubmit={run} className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Globe className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yourwebsite.com" className="pl-9" data-testid="seo-url-input" />
        </div>
        <Button type="submit" disabled={running} data-testid="seo-run-audit-button">{running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Search className="h-4 w-4 mr-1.5" />}{running ? "Scanning site…" : "Run audit"}</Button>
      </form>
      {!current ? (
        <div className="bg-card border border-border rounded-xl"><EmptyState icon={Search} title="No audits yet" text="Enter your website address. I'll scan the homepage and a few linked pages for SEO problems." /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8 space-y-4">
            <section className="bg-card border border-border rounded-xl p-5 flex flex-col sm:flex-row gap-5 sm:items-center" data-testid="seo-audit-summary">
              <ScoreRing score={current.score} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold break-all">{current.url}</p>
                <p className="text-xs text-muted-foreground">{current.pages.length} pages scanned · {fmtDateTime(current.created_at)}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {counts.map(([s, n]) => <span key={s} className={`text-xs rounded-full px-2.5 py-1 capitalize ${SEV[s]}`}>{n} {s}</span>)}
                  {Object.entries(current.site_checks || {}).map(([k, ok]) => (
                    <span key={k} className="text-xs rounded-full px-2.5 py-1 bg-muted flex items-center gap-1">{ok ? <CheckCircle2 className="h-3 w-3 text-[hsl(var(--success))]" /> : <XCircle className="h-3 w-3 text-destructive" />}{k}</span>
                  ))}
                </div>
              </div>
            </section>
            <Panel title="AI action plan" icon={Sparkles} testid="seo-plan-panel"
              action={<Button size="sm" variant={current.ai_summary ? "secondary" : "default"} onClick={plan} disabled={planning} data-testid="seo-plan-button">
                {planning ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{current.ai_summary ? "Rewrite plan" : "Get action plan"}</Button>}>
              {planning ? <p className="text-sm text-muted-foreground">Writing a prioritised plan. This usually takes 20–40 seconds.</p> :
                current.ai_summary ? <Markdown>{current.ai_summary}</Markdown> : <p className="text-sm text-muted-foreground">Turn these findings into a prioritised to-do list with content ideas.</p>}
            </Panel>
            <Panel title={`Issues (${current.issues.length})`} icon={AlertTriangle} testid="seo-issues">
              {current.issues.length === 0 ? <p className="text-sm text-muted-foreground">No issues found. Nice work.</p> : (
                <ul className="divide-y divide-border">
                  {[...current.issues].sort((a, b) => "hml".indexOf(a.severity[0]) - "hml".indexOf(b.severity[0])).map((i, idx) => (
                    <li key={idx} className="py-3 flex gap-3" data-testid="seo-issue-item">
                      <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 h-fit ${SEV[i.severity]}`}>{i.severity}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{i.message}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{i.fix}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 break-all">{i.page}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
          <div className="lg:col-span-4 space-y-4">
            <Panel title="Pages scanned" testid="seo-pages">
              <ul className="space-y-3">
                {current.pages.map((p) => (
                  <li key={p.url} className="text-xs">
                    <p className="font-medium break-all">{p.url.replace(/^https?:\/\//, "")}</p>
                    <p className="text-muted-foreground">HTTP {p.status} · {p.response_ms} ms · {p.word_count} words · {p.images_missing_alt}/{p.images} img no alt</p>
                    <p className="text-muted-foreground truncate">Title: {p.title || "—"}</p>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel title="Past audits" testid="seo-history">
              <ul className="space-y-1">
                {audits.map((a) => (
                  <li key={a.audit_id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${a.audit_id === current.audit_id ? "bg-muted" : "hover:bg-muted/70"}`}>
                    <button className="flex-1 min-w-0 text-left" onClick={() => setCurrent(a)} data-testid="seo-history-item">
                      <p className="text-xs font-medium truncate">{a.url.replace(/^https?:\/\//, "")}</p>
                      <p className="text-[11px] text-muted-foreground">Score {a.score} · {fmtDateTime(a.created_at)}</p>
                    </button>
                    <button onClick={() => remove(a)} aria-label="Delete audit" className="p-1 rounded hover:bg-card"><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></button>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Seo() {
  return (
    <UpgradeGate entitlement="ai_seo">
      <SeoInner />
    </UpgradeGate>
  );
}

function SeoInner() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "audit";
  return (
    <div data-testid="seo-page">
      <PageHeader title="AI SEO Specialist" subtitle="Website audits, keyword ideas, content briefs, SEO blog posts and page titles" />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList className="mb-4">
          <TabsTrigger value="audit" data-testid="seo-tab-audit">Site audit</TabsTrigger>
          <TabsTrigger value="content" data-testid="seo-tab-content">Keywords & content</TabsTrigger>
          <TabsTrigger value="chat" data-testid="seo-tab-chat">Chat</TabsTrigger>
        </TabsList>
        <TabsContent value="audit"><Audit /></TabsContent>
        <TabsContent value="content"><ContentLibrary area="seo" kinds={SEO_KINDS} /></TabsContent>
        <TabsContent value="chat">
          <AdvisorChat role="seo" className="h-[calc(100vh-15rem)] min-h-[520px]" persona={{
            name: "AI SEO Specialist", icon: Search, tagline: "Audits your site and writes search-friendly content",
            greeting: "Let's get you found on Google", intro: "I can scan your website, explain what's holding it back, suggest keywords (my estimates, not live ranking data) and write briefs, blog posts and page titles for you to approve and save.",
            placeholder: "e.g. Audit our website and tell me the top 5 fixes",
            suggestions: ["Audit our website and list the top 5 fixes", "Suggest 20 keyword ideas for our services", "Write a content brief for our most valuable keyword",
              "Write an SEO blog post about a common customer question", "Rewrite our homepage title and meta description", "What should our SEO plan be for the next 90 days?"],
          }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
